import { useCallback, useMemo } from 'react';

import { type ApolloClient } from '@apollo/client';
import { type Breakpoint } from '@mui/material';
import { generatedTextToEditorHtml } from '@realadvisor/html-sanitizer/generated-text';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { type ErrorOption, type FieldPath } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type {
  Lots_Set_Input,
  MandateFormFragment,
  Properties_Set_Input,
  PropertyFeaturesFragment,
  PropertyFormPricingFragment,
  PropertyPoiFragment,
  UpdateListingWithPropertyMutationVariables,
} from '@graphql/__generated__/graphql';
import {
  Address_Display_Options_Enum_Enum,
  Dictionaries_Types_Enum_Enum,
  Parking_Price_Type_Enum_Enum,
} from '@graphql/__generated__/graphql';
import type { Optional } from '@modules/_shared/common/types';
import { type RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import {
  type FormDefinitionType,
  type FormFieldDefinitionType,
  createCategoryElement,
  createCheckboxElement,
  createNumberFieldElement,
  createSelectElement,
  createTextFieldElement,
} from '@modules/_shared/components/form/RaForm';
import { FsboContactCard } from '@modules/_shared/components/listing-overview-form/FsboContactCard';
import {
  type ListingAvailability,
  ListingOverviewAvailabilitySelect,
} from '@modules/_shared/components/listing-overview-form/ListingOverviewAvailabilitySelect';
import { EEditListingForms } from '@modules/_shared/components/listing-overview-form/ListingOverviewEditModal';
import { ListingOverviewPublishDocumentField } from '@modules/_shared/components/listing-overview-form/ListingOverviewPublishDocumentField';
import {
  type FormDefinition,
  type LightFormDefinition,
  type TransformDataResult,
} from '@modules/_shared/components/listing-overview-form/models';
import {
  type AddressOverridesFormData,
  type ListedByOverridesFormData,
  getAddressOverridesDefinition,
  getListedByOverridesDefinition,
} from '@modules/_shared/components/listing-overview-form/sharedFormDefinition';
import { LotGenerateDescription } from '@modules/_shared/components/LotGenerateDescription';
import { LotGenerateTitle } from '@modules/_shared/components/LotGenerateTitle';
import { FetchDistancesButton } from '@modules/_shared/components/property-form/FetchDistancesButton';
import {
  type PropertyFormContext,
  type PropertyFormDetailsData,
  getDefaultPropertyDetailsFormValues,
  getPropertyDetailsFormDefinition,
  preparePropertyDetailsFormData,
} from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormDefinition';
import { getValidatePropertyDetailsFormData } from '@modules/_shared/components/property-form/forms-definitions/propertyDetailsFormValidation';
import {
  getDefaultPropertyFeaturesEquipmentsFormValues,
  preparePropertyFeaturesFormData,
  standaloneEquipmentFormDefinition,
  standaloneFeaturesFormDefinition,
} from '@modules/_shared/components/property-form/forms-definitions/propertyFeaturesDefinition';
import {
  getDefaultPropertyPoiFormValues,
  poiFormDefinition,
} from '@modules/_shared/components/property-form/forms-definitions/propertyPoiDefinition';
import {
  getDefaultPropertyPricingFormValues,
  getStandalonePricingFormDefinition,
  getValidatePricingFormData,
  preparePricingFormData,
} from '@modules/_shared/components/property-form/forms-definitions/propertyPricingDefinition';
import { UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING } from '@modules/_shared/components/PropertyFiles';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  EXPECTED_TIME_TO_SELL,
  MANDATE_TYPE,
  SUCCESS_FEE_TYPE,
  getExpectedTimeToSellList,
  getMandateTypeList,
  getSuccessFeeTypeList,
} from '@modules/_shared/utils/general-labels';
import { parseLotStatus } from '@modules/_shared/utils/lotChangeExtraData';
import { buildLotChangeActivities } from '@modules/_shared/utils/lotChangeRules';
import {
  isCommercialBuilding,
  isHouseOrAppt,
  isMixedUseBuilding,
} from '@modules/_shared/utils/propertyDataChecks';
import {
  getOfferTypeOptions,
  getPriceUnitOptions,
} from '@modules/_shared/utils/propertyTemplateFunctions';
import { type GetListingDetailsData } from '@modules/listings/lotsQueries';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { Currencies, type IntlLocale } from '@shared/LocaleContext';

type MutationFunction<TData, TVariables> = (
  options: { variables: TVariables } & Record<string, unknown>,
) => Promise<{ data?: TData | null }>;

type ListingData = Optional<
  Omit<GetListingDetailsData, 'property' | 'seller_notification_frequency'>,
  | 'id'
  | 'enquiries'
  | 'buyer_leads'
  | 'portal_listings'
  | 'enquiries_aggregate'
  | 'sellers'
  | 'has_success_fee'
  | 'success_fee_type'
  | 'expected_time_to_sell'
  | 'hide_price_on_portals'
  | 'use_dummy_contact'
  | 'fsbo_hide_phone'
  | 'fsbo_hide_last_name'
  | 'created_at'
  | 'status'
  | 'tenant_id'
  | 'is_parking_included_in_total'
  | 'address_display_option'
  | 'lot_reference_aliases'
> & {
  property: Optional<
    GetListingDetailsData['property'],
    | 'is_oriented_north'
    | 'is_oriented_south'
    | 'is_oriented_east'
    | 'is_oriented_west'
    | 'is_new_development'
  >;
};

export type TTitleDescriptionFormData = Pick<
  Lots_Set_Input,
  'title' | 'description'
>;
export type TAvailabilityFormData = Pick<Lots_Set_Input, 'available_from'> & {
  availability: ListingAvailability;
};
export type TPricingFormData = Pick<
  Lots_Set_Input,
  | 'hide_price_on_portals'
  | 'offer_type'
  | 'currency'
  | 'price_unit'
  | 'sale_price'
  | 'minimum_net_seller_price'
  | 'rent_net'
  | 'rent_extra'
  | 'parking_price'
  | 'is_parking_included_in_total'
  | 'parking_price_type'
  | 'indoor_parking_price'
  | 'outdoor_parking_price'
> & { property: PropertyFormPricingFragment };

export type TOtherMediaFormData = Pick<
  Lots_Set_Input,
  'virtual_visit_url' | 'video_url'
> & { file_to_publish?: string | null | undefined };
export type TListedByFormData = Pick<Lots_Set_Input, 'broker_id'> &
  ListedByOverridesFormData;

export type TPropertyAddressFormData = AddressOverridesFormData & {
  property?: RaAddressType;
};

export const MANDATE_FORM_FRAGMENT = gql(/* GraphQL */ `
  fragment MandateForm on lots {
    ...ListingMandateCard
    mandate_sale_price
    is_sales_tax_inclusive
    duration_in_months
    origin_id
    has_success_fee
    success_fee_type
    success_fee_custom
    success_fee_percentage
  }
`);

export type TMandateFormData = Pick<
  Lots_Set_Input,
  Exclude<keyof MandateFormFragment, 'property' | 'origin'>
>;

// Rented-room fields live across both tables: rental/offer terms on the lot,
// physical descriptors on the property. transformData splits them back apart.
export type TRentedRoomFormData = Pick<
  Lots_Set_Input,
  | 'tenant_number'
  | 'smoking_allowed'
  | 'couples_allowed'
  | 'pets_allowed'
  | 'tenant_gender'
  | 'youngest_tenant_age'
  | 'oldest_tenant_age'
  | 'minimal_stay'
  | 'occupied_now'
  | 'furnished'
  | 'owner_living'
> &
  Pick<Properties_Set_Input, 'bed_type' | 'window_view'>;

const getFormDefinitionMap = (
  listing: ListingData,
  displayMinergieCode: boolean,
  country: string,
  roomsAllowsDecimals: boolean,
  locale: IntlLocale,
) => {
  // Set default values.
  const listingWithDefaults = {
    is_parking_included_in_total: true,
    address_display_option: Address_Display_Options_Enum_Enum.HideStreet,
    ...listing,
    property: {
      is_oriented_north: false,
      is_oriented_south: false,
      is_oriented_east: false,
      is_oriented_west: false,
      is_new_development: false,
      ...listing.property,
    },
  };

  const propertyFiles = listingWithDefaults.property.property_files;
  const defaultVideoFile = propertyFiles.find(
    pFile =>
      pFile.display_on_listing && pFile.file.document_type?.name === 'video',
  );

  const defaultFileToPublish = propertyFiles.find(
    pFile =>
      pFile.display_on_listing && pFile.file.document_type?.name !== 'video',
  );

  return {
    [EEditListingForms.TITLE_DESCRIPTION]:
      (): LightFormDefinition<TTitleDescriptionFormData> => ({
        hasContext: false,
        formDefinition: ({ t }) => [
          {
            name: 'title',
            label: t('Title'),
            type: 'text',
            gridProps: { md: 12 },
            actions: ({ setValue }) => (
              <LotGenerateTitle
                lot_id={listingWithDefaults.id}
                onComplete={(title: string) => {
                  setValue('title', title, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            ),
          },
          {
            name: 'description',
            label: t('Description'),
            type: 'rich-text',
            format: 'html',
            preserveLineBreaks: true,
            gridProps: { md: 12 },
            actions: ({ setValue }) => (
              <LotGenerateDescription
                lot_id={listingWithDefaults.id}
                description={listingWithDefaults.description ?? ''}
                onComplete={(description: string) => {
                  setValue(
                    'description',
                    generatedTextToEditorHtml(description),
                    {
                      shouldDirty: true,
                      shouldValidate: true,
                    },
                  );
                }}
              />
            ),
          },
        ],
        defaultValues: {
          title: listingWithDefaults.title,
          description: listingWithDefaults.description,
        },
        transformData: ({ title, description }) => ({
          lot: { title, description },
        }),
      }),
    [EEditListingForms.AVAILABILITY]:
      (): LightFormDefinition<TAvailabilityFormData> => {
        let defaultAvailability: ListingAvailability = 'available_at';

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (listing.available_from == null) {
          defaultAvailability = 'by_request';
        } else {
          const availableFromDate = new Date(listing.available_from);
          availableFromDate.setHours(0, 0, 0, 0);

          if (availableFromDate <= today) {
            defaultAvailability = 'available_now';
          }
        }

        return {
          hasContext: false,
          formDefinition: ({ t }) => [
            {
              type: 'custom',
              name: 'availability',
              element: <ListingOverviewAvailabilitySelect />,
              gridProps: { md: 12 },
            },
            {
              name: 'available_from',
              label: t('Available from'),
              type: 'date',
              gridProps: { md: 12 },
              required: true,
              render: ({ availability }: TAvailabilityFormData) =>
                availability === 'available_at',
            },
          ],
          defaultValues: {
            available_from: listing.available_from,
            availability: defaultAvailability,
          },
          transformData: ({ available_from }) => ({ lot: { available_from } }),
        };
      },
    [EEditListingForms.PRICING]: (): LightFormDefinition<TPricingFormData> => ({
      hasContext: false,
      formDefinition: ({ t }) => {
        const definition: ReturnType<FormDefinitionType<TPricingFormData>> = [
          createCategoryElement('offer_and_price_title', t('Offer & price')),
          createSelectElement(
            'offer_type',
            t('offerType'),
            () =>
              getOfferTypeOptions(t)
                .map(offerType => ({
                  value: offerType.id,
                  label: offerType.label,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            { gridProps: { xs: 6 } },
          ),
          createSelectElement(
            'currency',
            t('currency'),
            () =>
              Currencies.map(currency => ({
                value: currency,
                label: currency,
              })).sort((a, b) => a.label.localeCompare(b.label)),
            { gridProps: { xs: 6 }, required: true },
          ),
          createSelectElement(
            'price_unit',
            t('priceUnit'),
            ({ offer_type }) =>
              getPriceUnitOptions(t, offer_type).sort((a, b) =>
                a.label.localeCompare(b.label),
              ),
            { gridProps: { xs: 6 } },
          ),
          {
            name: 'sale_price',
            label: t('salePrice'),
            type: 'number',
            render: ({ offer_type }) => offer_type === 'sell',
            gridProps: { xs: 6 },
          },
          {
            name: 'minimum_net_seller_price',
            label: t('minimumNetSellerPrice'),
            type: 'number',
            render: ({ offer_type }) => offer_type === 'sell',
            gridProps: { xs: 6 },
          },
          {
            name: 'rent_net',
            label: t('rentNet'),
            type: 'number',
            render: ({ offer_type }) => offer_type === 'rent',
            gridProps: { xs: 6 },
          },
          {
            name: 'rent_extra',
            label: t('rentExtra'),
            type: 'number',
            gridProps: { xs: 6 },
            render: ({ offer_type }) => offer_type === 'rent',
          },
          createCategoryElement('visibility_title', t('Visibility on portals')),
          {
            name: 'hide_price_on_portals',
            label: t('hidePriceOnPortals'),
            type: 'checkbox',
            style: 'switch',
            gridProps: { md: 12 },
          },
          createCategoryElement(
            'parking_title',
            t('Parking'),
            () =>
              isHouseOrAppt(listingWithDefaults.property.__property_type) ||
              isMixedUseBuilding(
                listingWithDefaults.property.__property_type,
              ) ||
              isCommercialBuilding(
                listingWithDefaults.property.__property_type,
              ),
          ),
          createCheckboxElement(
            'is_parking_included_in_total',
            t('Parking price included in total'),
            {
              gridProps: { xs: 6 },
              style: 'switch',
              helpText: t(
                'If your property does not have a parking space/slot, just let this option checked.',
              ),
              render: () =>
                isHouseOrAppt(listingWithDefaults.property.__property_type) ||
                isMixedUseBuilding(
                  listingWithDefaults.property.__property_type,
                ) ||
                isCommercialBuilding(
                  listingWithDefaults.property.__property_type,
                ),
            },
          ),
          createSelectElement(
            'parking_price_type',
            t('Price type'),
            () => [
              { value: 'total_price', label: t('Total price') },
              { value: 'per_space_price', label: t('Price per parking space') },
            ],
            {
              gridProps: { xs: 6 },
              render: ({ is_parking_included_in_total }) =>
                !is_parking_included_in_total &&
                (isHouseOrAppt(listingWithDefaults.property.__property_type) ||
                  isMixedUseBuilding(
                    listingWithDefaults.property.__property_type,
                  ) ||
                  isCommercialBuilding(
                    listingWithDefaults.property.__property_type,
                  )),
            },
          ),
          createNumberFieldElement('parking_price', t('Parking price'), {
            gridProps: { xs: 6 },
            render: ({ is_parking_included_in_total, parking_price_type }) =>
              !is_parking_included_in_total &&
              parking_price_type === Parking_Price_Type_Enum_Enum.TotalPrice &&
              (isHouseOrAppt(listingWithDefaults.property.__property_type) ||
                isMixedUseBuilding(
                  listingWithDefaults.property.__property_type,
                ) ||
                isCommercialBuilding(
                  listingWithDefaults.property.__property_type,
                )),
          }),
          createNumberFieldElement(
            'indoor_parking_price',
            t('Price per indoor parking space'),
            {
              gridProps: { xs: 6 },
              render: ({ is_parking_included_in_total, parking_price_type }) =>
                !is_parking_included_in_total &&
                parking_price_type ===
                  Parking_Price_Type_Enum_Enum.PerSpacePrice &&
                (isHouseOrAppt(listingWithDefaults.property.__property_type) ||
                  isMixedUseBuilding(
                    listingWithDefaults.property.__property_type,
                  ) ||
                  isCommercialBuilding(
                    listingWithDefaults.property.__property_type,
                  )),
            },
          ),
          createNumberFieldElement(
            'outdoor_parking_price',
            t('Price per outdoor parking space'),
            {
              gridProps: { xs: 6 },
              render: ({ is_parking_included_in_total, parking_price_type }) =>
                !is_parking_included_in_total &&
                parking_price_type ===
                  Parking_Price_Type_Enum_Enum.PerSpacePrice &&
                (isHouseOrAppt(listingWithDefaults.property.__property_type) ||
                  isMixedUseBuilding(
                    listingWithDefaults.property.__property_type,
                  ) ||
                  isCommercialBuilding(
                    listingWithDefaults.property.__property_type,
                  )),
            },
          ),
        ];

        const pricingFormDefinition =
          getStandalonePricingFormDefinition('property');
        definition.push(
          ...pricingFormDefinition({
            t,
            context: {
              propertyType: listingWithDefaults.property.__property_type,
            },
          }),
        );

        return definition;
      },
      defaultValues: {
        hide_price_on_portals:
          listingWithDefaults.hide_price_on_portals ?? false,
        sale_price: listingWithDefaults.sale_price,
        minimum_net_seller_price: listingWithDefaults.minimum_net_seller_price,
        rent_net: listingWithDefaults.rent_net,
        rent_extra: listingWithDefaults.rent_extra,
        price_unit: listingWithDefaults.price_unit ?? 'sell',
        offer_type: listingWithDefaults.offer_type ?? 'sell',
        currency: listingWithDefaults.currency,
        parking_price: listingWithDefaults.parking_price,
        parking_price_type:
          listingWithDefaults.parking_price_type ??
          Parking_Price_Type_Enum_Enum.TotalPrice,
        indoor_parking_price: listingWithDefaults.indoor_parking_price,
        outdoor_parking_price: listingWithDefaults.outdoor_parking_price,
        is_parking_included_in_total:
          listingWithDefaults.is_parking_included_in_total,
        property: getDefaultPropertyPricingFormValues(
          listingWithDefaults.property,
        ),
      },
      transformData: data => {
        const {
          currency,
          offer_type,
          price_unit,
          minimum_net_seller_price,
          sale_price: salePriceInput,
          rent_extra: rentExtraInput,
          rent_net: rentNetInput,
          hide_price_on_portals,
          property,
          parking_price,
          parking_price_type,
          indoor_parking_price,
          outdoor_parking_price,
          is_parking_included_in_total,
        } = data;

        let rentNet;
        let rentExtra;
        let salePrice;
        let parkingPrice = parking_price;
        let indoorParkingPrice = indoor_parking_price;
        let outdoorParkingPrice = outdoor_parking_price;
        let parkingPriceType = parking_price_type;

        if (offer_type === 'sell') {
          salePrice = salePriceInput;
          rentExtra = null;
          rentNet = null;
        } else {
          salePrice = null;
          rentExtra = rentExtraInput;
          rentNet = rentNetInput;
        }

        const propertyPayload = preparePricingFormData(
          property,
          listingWithDefaults.property.__property_type,
        );

        if (is_parking_included_in_total) {
          parkingPrice = null;
          indoorParkingPrice = null;
          outdoorParkingPrice = null;
          parkingPriceType = null;
        } else if (
          parkingPriceType === Parking_Price_Type_Enum_Enum.TotalPrice
        ) {
          indoorParkingPrice = null;
          outdoorParkingPrice = null;
        } else if (
          parkingPriceType === Parking_Price_Type_Enum_Enum.PerSpacePrice
        ) {
          parkingPrice = null;
        }

        return {
          lot: {
            offer_type,
            currency,
            price_unit,
            rent_net: rentNet,
            rent_extra: rentExtra,
            minimum_net_seller_price,
            sale_price: salePrice,
            hide_price_on_portals,
            parking_price: parkingPrice,
            parking_price_type: parkingPriceType,
            indoor_parking_price: indoorParkingPrice,
            outdoor_parking_price: outdoorParkingPrice,
            is_parking_included_in_total,
          },
          property: propertyPayload,
        };
      },
    }),
    [EEditListingForms.MEDIAS]:
      (): LightFormDefinition<TOtherMediaFormData> => ({
        hasContext: false,
        formDefinition: ({ t }) => [
          {
            name: 'video_url',
            type: 'custom',
            element: (
              <ListingOverviewPublishDocumentField
                name="video_url"
                propertyFiles={propertyFiles}
                fileType="videos"
                allowFreeText
                valueProp="url"
              />
            ),
          },
          {
            name: 'file_to_publish',
            type: 'custom',
            element: (
              <ListingOverviewPublishDocumentField
                name="file_to_publish"
                propertyFiles={propertyFiles}
              />
            ),
          },
          {
            name: 'virtual_visit_url',
            label: t('Virtual visit URL'),
            type: 'text',
          },
        ],
        defaultValues: {
          virtual_visit_url: listingWithDefaults.virtual_visit_url,
          video_url:
            defaultVideoFile?.file.url ?? listingWithDefaults.video_url,
          file_to_publish: defaultFileToPublish?.file_id,
        },
        transformData: ({ virtual_visit_url, video_url }) => ({
          lot: { virtual_visit_url, video_url },
        }),
        afterSubmit: async (data, _, { apolloClient }) => {
          const { file_to_publish, video_url } = data;

          if (defaultFileToPublish?.file_id !== file_to_publish) {
            if (file_to_publish != null) {
              // Set display_on_listing to true for the selected file and false for all others
              await apolloClient.mutate({
                mutation: UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING,
                variables: {
                  file_id: file_to_publish,
                  property_id: listingWithDefaults.property.id,
                  display: true,
                  should_reset_non_video: true,
                  should_reset_video: false,
                },
              });
            } else if (defaultFileToPublish?.file_id != null) {
              // If no file is selected, set display_on_listing to false for all files
              await apolloClient.mutate({
                mutation: UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING,
                variables: {
                  file_id: defaultFileToPublish.file_id,
                  property_id: listingWithDefaults.property.id,
                  display: false,
                  should_reset_non_video: false,
                  should_reset_video: false,
                },
              });
            }
          }

          if (defaultVideoFile?.file.url !== video_url) {
            if (video_url != null) {
              const videoFile = propertyFiles.find(
                file => file.file.url === video_url,
              );

              await apolloClient.mutate({
                mutation: UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING,
                variables: {
                  // Use a dummy uuid to reset all video files
                  file_id:
                    videoFile?.file_id ??
                    '00000000-0000-0000-0000-000000000000',
                  property_id: listingWithDefaults.property.id,
                  display: true,
                  should_reset_non_video: false,
                  should_reset_video: true,
                },
              });
            } else if (defaultVideoFile?.file_id != null) {
              // If no video is selected, set display_on_listing to false for all files
              await apolloClient.mutate({
                mutation: UPDATE_PROPERTY_FILE_DISPLAY_ON_LISTING,
                variables: {
                  file_id: defaultVideoFile.file_id,
                  property_id: listingWithDefaults.property.id,
                  display: false,
                  should_reset_non_video: false,
                  should_reset_video: false,
                },
              });
            }
          }
        },
      }),
    [EEditListingForms.LISTED_BY]:
      (): LightFormDefinition<TListedByFormData> => {
        // FSBO listings have no broker or agency — the contact is the seller
        // (use_dummy_contact false) or a manually entered person
        // (use_dummy_contact true). The dialog drops the broker picker and
        // every agency field, and adds the fsbo_hide_phone / fsbo_hide_last_name toggles.
        const isFsbo = listingWithDefaults.origin?.name === 'fsbo';

        if (isFsbo) {
          const seller = listingWithDefaults.sellers?.[0]?.seller ?? null;

          return {
            hasContext: false,
            formDefinition: ({ t }) => [
              {
                name: '',
                type: 'custom',
                gridProps: { md: 12 },
                element: <FsboContactCard seller={seller} />,
              },
              {
                name: 'dummy_broker_name',
                label: t('Contact name'),
                type: 'text',
                gridProps: { md: 12 },
                required: true,
                render: ({ use_dummy_contact }) => use_dummy_contact === true,
              },
              {
                name: 'dummy_broker_phone',
                label: t('Contact phone'),
                type: 'phone_number',
                gridProps: { md: 12 },
                required: true,
                render: ({ use_dummy_contact }) => use_dummy_contact === true,
              },
              {
                name: 'fsbo_hide_phone',
                label: t('Hide the advertiser’s phone number on the listing'),
                type: 'checkbox',
                style: 'switch',
                gridProps: { md: 12 },
                // Only meaningful for the advertiser's own phone — a
                // manually entered contact is always published in full.
                render: ({ use_dummy_contact }) => use_dummy_contact !== true,
              },
              {
                name: 'fsbo_hide_last_name',
                label: t('Hide the advertiser’s last name on the listing'),
                type: 'checkbox',
                style: 'switch',
                gridProps: { md: 12 },
                // On → the listing shows the first name and last initial only
                // ("John S."). A manually entered contact is always published
                // in full, so this only applies to the advertiser's own name.
                render: ({ use_dummy_contact }) => use_dummy_contact !== true,
              },
            ],
            defaultValues: {
              use_dummy_contact: listingWithDefaults.use_dummy_contact ?? false,
              fsbo_hide_phone: listingWithDefaults.fsbo_hide_phone ?? false,
              fsbo_hide_last_name:
                listingWithDefaults.fsbo_hide_last_name ?? false,
              dummy_broker_name: listingWithDefaults.dummy_broker_name,
              dummy_broker_phone: listingWithDefaults.dummy_broker_phone,
            },
            transformData: data => ({
              lot: {
                use_dummy_contact: data.use_dummy_contact,
                fsbo_hide_phone: data.fsbo_hide_phone,
                fsbo_hide_last_name: data.fsbo_hide_last_name,
                dummy_broker_name: data.use_dummy_contact
                  ? data.dummy_broker_name
                  : null,
                dummy_broker_phone: data.use_dummy_contact
                  ? data.dummy_broker_phone
                  : null,
                // FSBO listings never have an agency — clear stale overrides.
                dummy_organisation_name: null,
                dummy_organisation_phone: null,
                dummy_organisation_route: null,
                dummy_organisation_street_number: null,
                dummy_organisation_locality: null,
                dummy_organisation_state: null,
                dummy_organisation_country_code: null,
                dummy_organisation_postcode: null,
                dummy_organisation_lng: null,
                dummy_organisation_lat: null,
              },
            }),
          };
        }

        const overrideDefinition = getListedByOverridesDefinition(
          listingWithDefaults,
          country,
        );

        return {
          hasContext: false,
          formDefinition: ({ t }) => [
            {
              name: 'broker_id',
              label: t('broker'),
              type: 'user',
              required: true,
            },
            {
              name: '',
              type: 'category-title',
              label: t('displayContactOptions'),
            },
            ...overrideDefinition.formDefinition({ t, context: undefined }),
          ],
          defaultValues: {
            broker_id: listingWithDefaults.broker?.id,
            ...overrideDefinition.defaultValues,
          },
          transformData: data => {
            const { broker_id } = data;

            return {
              lot: {
                broker_id,
                ...(overrideDefinition.transformData(data).lot ?? {}),
              },
            };
          },
        };
      },
    [EEditListingForms.MANDATE]: (): LightFormDefinition<TMandateFormData> => ({
      hasContext: false,
      formDefinition: ({ t }) => [
        {
          name: 'mandate_sale_price',
          label: t('Desired sale price'),
          type: 'number',
          gridProps: { xs: 12, sm: 6, md: 4 },
        },
        createSelectElement(
          'mandate_type',
          t('Mandate type'),
          () => getMandateTypeList(t),
          { gridProps: { xs: 12, sm: 6, md: 4 } },
        ),
        createNumberFieldElement('commission_rate', t('Rate'), {
          suffix: '%',
          gridProps: { xs: 12, sm: 6, md: 4 },
          decimalNumbers: 1,
          render: ({ mandate_type }: TMandateFormData) =>
            mandate_type === MANDATE_TYPE.COMMISSION_BASED,
        }),
        createNumberFieldElement('fixed_fee', t('Fixed fee'), {
          gridProps: { xs: 12, sm: 6, md: 4 },
          render: ({ mandate_type }: TMandateFormData) =>
            mandate_type === MANDATE_TYPE.FIXED_FEE,
        }),
        {
          name: 'signed_at',
          type: 'date',
          label: t('Signed at'),
          gridProps: { xs: 12, sm: 6, md: 4 },
        },
        {
          name: 'is_exclusive',
          type: 'checkbox',
          label: t('Exclusive listing'),
          gridProps: { xs: 12, sm: 6, md: 4 },
        },
        {
          name: 'is_sales_tax_inclusive',
          type: 'checkbox',
          label: t('Sales tax inclusive'),
          gridProps: { xs: 12, sm: 6, md: 4 },
        },
        createNumberFieldElement('duration_in_months', t('Duration'), {
          suffix: t('months'),
          gridProps: { xs: 12, sm: 6, md: 4 },
        }),
        createSelectElement(
          'expected_time_to_sell',
          t('Expected time to sell'),
          () => getExpectedTimeToSellList(t),
          { gridProps: { xs: 12, sm: 6, md: 4 } },
        ),
        {
          name: 'origin_id',
          label: t('Mandate origin'),
          type: 'dictionary',
          valueField: 'id',
          dictionaryType: Dictionaries_Types_Enum_Enum.LotOriginTypes,
          multiple: false,
          gridProps: { xs: 12, sm: 6, md: 4 },
        },
        {
          name: 'has_success_fee',
          type: 'checkbox',
          label: t('Success fee'),
          gridProps: { md: 12 },
        },
        createSelectElement(
          'success_fee_type',
          t('Success fee type'),
          () => getSuccessFeeTypeList(t),
          {
            render: ({ has_success_fee }: TMandateFormData) =>
              has_success_fee ?? false,
            gridProps: { xs: 12, sm: 6 },
          },
        ),
        createNumberFieldElement('success_fee_percentage', t('Percentage'), {
          suffix: '%',
          gridProps: { xs: 12, sm: 6 },
          decimalNumbers: 1,
          render: ({ success_fee_type, has_success_fee }: TMandateFormData) =>
            !!has_success_fee &&
            success_fee_type ===
              SUCCESS_FEE_TYPE.PERCENTAGE_OF_PROCEEDS_ABOVE_SALE_PRICE,
        }),
        createTextFieldElement(
          'success_fee_custom',
          t('Custom success fee structure'),
          {
            gridProps: { xs: 12, sm: 6 },
            render: ({ success_fee_type, has_success_fee }: TMandateFormData) =>
              !!has_success_fee && success_fee_type === SUCCESS_FEE_TYPE.CUSTOM,
          },
        ),
      ],
      defaultValues: {
        expected_time_to_sell:
          listingWithDefaults.expected_time_to_sell ??
          EXPECTED_TIME_TO_SELL.NOT_SET,
        mandate_sale_price: listingWithDefaults.mandate_sale_price,
        is_sales_tax_inclusive:
          listingWithDefaults.is_sales_tax_inclusive ?? false,
        duration_in_months: listingWithDefaults.duration_in_months,
        origin_id: listingWithDefaults.origin_id,
        has_success_fee: listingWithDefaults.has_success_fee ?? false,
        success_fee_type: listingWithDefaults.success_fee_type,
        success_fee_custom: listingWithDefaults.success_fee_custom,
        success_fee_percentage:
          listingWithDefaults.success_fee_percentage != null
            ? listingWithDefaults.success_fee_percentage * 100
            : listingWithDefaults.success_fee_percentage,
        currency: listingWithDefaults.currency,
        commission_rate:
          listingWithDefaults.commission_rate != null
            ? listingWithDefaults.commission_rate * 100
            : listingWithDefaults.commission_rate,
        fixed_fee: listingWithDefaults.fixed_fee,
        is_exclusive: listingWithDefaults.is_exclusive ?? false,
        mandate_type:
          listingWithDefaults.mandate_type ?? MANDATE_TYPE.COMMISSION_BASED,
        signed_at: listingWithDefaults.signed_at,
      },
      transformData: ({
        success_fee_percentage,
        commission_rate,
        ...otherProps
      }) => ({
        lot: {
          success_fee_percentage:
            success_fee_percentage != null
              ? success_fee_percentage / 100
              : null,
          commission_rate:
            commission_rate != null ? commission_rate / 100 : null,
          ...otherProps,
        },
      }),
    }),
    [EEditListingForms.ADDRESS]:
      (): LightFormDefinition<TPropertyAddressFormData> => {
        const overrideDefinition = getAddressOverridesDefinition(
          listingWithDefaults,
          country,
        );

        return {
          hasContext: false,
          formDefinition: ({ t }) => [
            {
              label: t('Address'),
              path: 'property',
              type: 'address',
              countryRestriction: country,
              gridProps: { xs: 12 },
            },
            createCategoryElement(
              'display_address_options',
              t('displayAddressOptions'),
            ),
            ...overrideDefinition.formDefinition({ t, context: undefined }),
          ],
          defaultValues: {
            ...overrideDefinition.defaultValues,
            property: {
              street_number: listingWithDefaults.property.street_number,
              route: listingWithDefaults.property.route,
              locality: listingWithDefaults.property.locality,
              postcode: listingWithDefaults.property.postcode,
              state: listingWithDefaults.property.state,
              country: listingWithDefaults.property.country,
              country_code: listingWithDefaults.property.country_code,
              lat: listingWithDefaults.property.lat,
              lng: listingWithDefaults.property.lng,
            },
          },
          transformData: ({ property, ...data }) => ({
            lot: overrideDefinition.transformData(data).lot,
            property,
          }),
        };
      },
    [EEditListingForms.POI]: (): LightFormDefinition<PropertyPoiFragment> => ({
      hasContext: false,
      formDefinition: ({ t, context }) => [
        {
          type: 'custom',
          name: 'fetch_distances_button',
          gridProps: { xs: 12, md: 12 },
          element: (
            <FetchDistancesButton
              propertyId={listingWithDefaults.property.id}
            />
          ),
        },
        ...poiFormDefinition({ t, context }),
      ],
      defaultValues: getDefaultPropertyPoiFormValues(
        listingWithDefaults.property,
      ),
      transformData: data => ({
        property: data,
      }),
    }),
    [EEditListingForms.EQUIPMENT]:
      (): LightFormDefinition<PropertyFeaturesFragment> => ({
        hasContext: false,
        formDefinition: ({ t }) => {
          const equipmentFields = standaloneEquipmentFormDefinition({
            t,
            context: {
              propertyType: listingWithDefaults.property.__property_type,
            },
          });
          const featuresFields = standaloneFeaturesFormDefinition({
            t,
            context: undefined,
          });

          return [
            ...equipmentFields,
            ...featuresFields,
          ] as FormFieldDefinitionType<PropertyFeaturesFragment>[];
        },
        defaultValues: getDefaultPropertyFeaturesEquipmentsFormValues(
          listingWithDefaults.property,
        ),
        transformData: data => ({
          property: preparePropertyFeaturesFormData(
            data,
            listingWithDefaults.property.__property_type,
          ),
        }),
      }),
    [EEditListingForms.PROPERTY]: (): LightFormDefinition<
      PropertyFormDetailsData,
      PropertyFormContext
    > => ({
      hasContext: true,
      formDefinition: getPropertyDetailsFormDefinition(
        displayMinergieCode,
        listingWithDefaults.currency,
        listingWithDefaults.property.country_code ?? country,
        roomsAllowsDecimals,
        locale,
      ),
      defaultValues: getDefaultPropertyDetailsFormValues(
        listingWithDefaults.property,
      ),
      transformData: data => ({
        property: preparePropertyDetailsFormData(data, displayMinergieCode),
      }),
    }),
    [EEditListingForms.RENTED_ROOM]: (): LightFormDefinition<
      TRentedRoomFormData,
      PropertyFormContext
    > => ({
      hasContext: true,
      formDefinition: ({ t, context }) => [
        createCategoryElement('rented_room_terms', t('Rental terms')),
        createNumberFieldElement(
          'tenant_number',
          t('Max number of flat mates'),
          {
            gridProps: { xs: 12, sm: 6 },
            min: 2,
            max: 99,
          },
        ),
        createNumberFieldElement('minimal_stay', t('Minimal stay (months)'), {
          gridProps: { xs: 12, sm: 6 },
          min: 1,
          max: 6,
        }),
        createCheckboxElement('furnished', t('Furnished'), {
          style: 'switch',
          gridProps: { xs: 12, sm: 6 },
        }),
        createCheckboxElement('smoking_allowed', t('Smoking allowed'), {
          style: 'switch',
          gridProps: { xs: 12, sm: 6 },
        }),
        createCheckboxElement('couples_allowed', t('Couples allowed'), {
          style: 'switch',
          gridProps: { xs: 12, sm: 6 },
        }),
        createCheckboxElement('pets_allowed', t('Pets allowed'), {
          style: 'switch',
          gridProps: { xs: 12, sm: 6 },
        }),
        createCategoryElement('rented_room_occupancy', t('Occupancy')),
        createCheckboxElement('occupied_now', t('Currently occupied'), {
          style: 'switch',
          gridProps: { xs: 12, alignSelf: 'flex-start' },
        }),
        {
          ...createSelectElement(
            'tenant_gender',
            t('Flatmates gender'),
            () =>
              (context.tenantGenderOptions ?? []).map(o => ({
                value: o.value,
                label: o.label ?? o.value,
              })),
            {
              gridProps: { xs: 12, sm: 6 },
              render: ({ occupied_now }) => occupied_now === true,
            },
          ),
          clearable: true,
        },
        createNumberFieldElement(
          'youngest_tenant_age',
          t('Youngest flatmate age'),
          {
            gridProps: { xs: 12, sm: 6 },
            render: ({ occupied_now }) => occupied_now === true,
            min: 1,
            max: 99,
          },
        ),
        createNumberFieldElement(
          'oldest_tenant_age',
          t('Oldest flatmate age'),
          {
            gridProps: { xs: 12, sm: 6 },
            render: ({ occupied_now }) => occupied_now === true,
            min: 1,
            max: 99,
          },
        ),
        createCheckboxElement(
          'owner_living',
          t('Owner lives in the property'),
          {
            style: 'switch',
            gridProps: { xs: 12 },
            render: ({ occupied_now }) => occupied_now === true,
          },
        ),
        createCategoryElement('rented_room_physical', t('Room')),
        {
          ...createSelectElement(
            'bed_type',
            t('Bed type'),
            () =>
              (context.bedTypeOptions ?? []).map(o => ({
                value: o.value,
                label: o.label ?? o.value,
              })),
            { gridProps: { xs: 12, sm: 6 } },
          ),
          clearable: true,
        },
        {
          ...createSelectElement(
            'window_view',
            t('Window view'),
            () =>
              (context.windowViewOptions ?? []).map(o => ({
                value: o.value,
                label: o.label ?? o.value,
              })),
            { gridProps: { xs: 12, sm: 6 } },
          ),
          clearable: true,
        },
      ],
      defaultValues: {
        tenant_number: listingWithDefaults.tenant_number ?? null,
        smoking_allowed: listingWithDefaults.smoking_allowed ?? false,
        couples_allowed: listingWithDefaults.couples_allowed ?? false,
        pets_allowed: listingWithDefaults.pets_allowed ?? false,
        tenant_gender: listingWithDefaults.tenant_gender ?? null,
        youngest_tenant_age: listingWithDefaults.youngest_tenant_age ?? null,
        oldest_tenant_age: listingWithDefaults.oldest_tenant_age ?? null,
        minimal_stay: listingWithDefaults.minimal_stay ?? null,
        occupied_now: listingWithDefaults.occupied_now ?? false,
        furnished: listingWithDefaults.furnished ?? false,
        owner_living: listingWithDefaults.owner_living ?? false,
        bed_type: listingWithDefaults.property.bed_type ?? null,
        window_view: listingWithDefaults.property.window_view ?? null,
      },
      transformData: ({ bed_type, window_view, ...lot }) => {
        // If the room is not occupied, clear the tenant gender, youngest tenant age, oldest tenant age, and owner living.
        if (lot.occupied_now === false) {
          lot.tenant_gender = null;
          lot.youngest_tenant_age = null;
          lot.oldest_tenant_age = null;
          lot.owner_living = null;
        }

        return {
          lot,
          property: { bed_type, window_view },
        };
      },
    }),
  };
};

export const getListingOverviewEditFormDefinition = (
  t: Translate,
  listing: GetListingDetailsData,
  updateFn: MutationFunction<any, UpdateListingWithPropertyMutationVariables>,
  displayMinergieCode: boolean,
  roomsAllowsDecimals: boolean,
  locale: IntlLocale,
  country: string,
  apolloClient?: ApolloClient,
  sessionUserId?: string | null,
) => {
  // Allows to leverage TFormData typing for each form definition.
  const getFormDefinition = <
    TFormData extends Record<string, any>,
    TContext extends Record<string, any> | undefined = undefined,
  >(
    title: string,
    formDefinition: LightFormDefinition<TFormData, TContext>,
    validate?:
      | ((data: TFormData) => [FieldPath<TFormData>, ErrorOption][])
      | null,
    modalMaxWidth?: Breakpoint,
  ): FormDefinition<TFormData, TContext> => {
    const definition: FormDefinition<TFormData, TContext> = {
      title,
      hasContext: formDefinition.hasContext as any,
      formDefinition: formDefinition.formDefinition,
      defaultValues: formDefinition.defaultValues,
      onSubmit: async data => {
        const transformedData = formDefinition.transformData(data);
        const { lot, property } = transformedData;

        const lotSet = lot ?? {};
        const activities = buildLotChangeActivities(
          {
            status: parseLotStatus(listing.status),
            offer_type: listing.offer_type,
            sale_price: listing.sale_price,
            rent_net: listing.rent_net,
            rent_extra: listing.rent_extra,
          },
          lotSet,
          {
            lotId: listing.id,
            tenantId: listing.tenant_id,
            sessionUserId: sessionUserId ?? null,
            currency: listing.currency,
          },
        );

        const result = await updateFn({
          variables: {
            id: listing.id,
            prop_id: listing.property.id,
            lot: lotSet,
            property: property ?? {},
            activities,
            hasActivities: activities.length > 0,
          },
        });

        // Execute any additional operations defined by the form
        if (formDefinition.afterSubmit != null && apolloClient != null) {
          await formDefinition.afterSubmit(
            data,
            transformedData as TransformDataResult,
            {
              apolloClient,
            },
          );
        }

        return result;
      },
      modalMaxWidth,
    };

    if (validate != null) {
      definition.validate = validate;
    }

    return definition;
  };

  const formDefinitionMap = getFormDefinitionMap(
    listing,
    displayMinergieCode,
    country,
    roomsAllowsDecimals,
    locale,
  );

  const listingFormDefinition = new Map<
    EEditListingForms,
    | FormDefinition<TTitleDescriptionFormData>
    | FormDefinition<TAvailabilityFormData>
    | FormDefinition<TPricingFormData>
    | FormDefinition<TOtherMediaFormData>
    | FormDefinition<TListedByFormData>
    | FormDefinition<TMandateFormData>
    | FormDefinition<TPropertyAddressFormData>
    | FormDefinition<PropertyFormDetailsData, PropertyFormContext>
    | FormDefinition<PropertyPoiFragment>
    | FormDefinition<PropertyFeaturesFragment>
    | FormDefinition<TRentedRoomFormData, PropertyFormContext>
  >([
    [
      EEditListingForms.TITLE_DESCRIPTION,
      getFormDefinition<TTitleDescriptionFormData>(
        t('Title and description'),
        formDefinitionMap[EEditListingForms.TITLE_DESCRIPTION](),
      ),
    ],
    [
      EEditListingForms.AVAILABILITY,
      getFormDefinition<TAvailabilityFormData>(
        t('Availability'),
        formDefinitionMap[EEditListingForms.AVAILABILITY](),
        null,
        'xs',
      ),
    ],
    [
      EEditListingForms.PRICING,
      getFormDefinition<TPricingFormData>(
        t('Price'),
        formDefinitionMap[EEditListingForms.PRICING](),
        data => {
          const errors: [FieldPath<TPricingFormData>, ErrorOption][] = [];
          const validateErrors = getValidatePricingFormData(
            t,
            listing.property.__property_type,
            'property',
          )(data);
          errors.push(...validateErrors);

          if (
            !data.is_parking_included_in_total &&
            data.parking_price_type ===
              Parking_Price_Type_Enum_Enum.TotalPrice &&
            data.parking_price == null
          ) {
            errors.push([
              'parking_price',
              {
                message: t(
                  'Parking price is required if parking is not included in price',
                ),
              },
            ]);
          }

          if (
            !data.is_parking_included_in_total &&
            data.parking_price_type ===
              Parking_Price_Type_Enum_Enum.PerSpacePrice &&
            data.indoor_parking_price == null &&
            data.outdoor_parking_price == null
          ) {
            errors.push([
              'indoor_parking_price',
              {
                message: t('At least one parking space price is required'),
              },
            ]);
            errors.push([
              'outdoor_parking_price',
              {
                message: t('At least one parking space price is required'),
              },
            ]);
          }

          return errors;
        },
      ),
    ],
    [
      EEditListingForms.MEDIAS,
      getFormDefinition<TOtherMediaFormData>(
        t('Other media'),
        formDefinitionMap[EEditListingForms.MEDIAS](),
      ),
    ],
    [
      EEditListingForms.LISTED_BY,
      getFormDefinition<TListedByFormData>(
        t('Listed by'),
        formDefinitionMap[EEditListingForms.LISTED_BY](),
        // FSBO + manual contact override: empty input is gated by the
        // field's required prop; anything non-empty (including
        // whitespace-only, which would otherwise pass `required`) must
        // parse as a valid phone number.
        data => {
          if (
            listing.origin?.name !== 'fsbo' ||
            data.use_dummy_contact !== true
          ) {
            return [];
          }
          const raw = data.dummy_broker_phone ?? '';
          if (raw === '') {
            return [];
          }
          const parsed = parsePhoneNumberFromString(raw.trim());
          if (parsed != null && parsed.isValid()) {
            return [];
          }
          return [
            ['dummy_broker_phone', { message: t('Invalid phone number') }],
          ];
        },
        // FSBO uses the compact tabs + preview card, so it fits a narrower
        // dialog; the broker/agency variant keeps the default width.
        listing.origin?.name === 'fsbo' ? 'sm' : undefined,
      ),
    ],
    [
      EEditListingForms.MANDATE,
      getFormDefinition<TMandateFormData>(
        t('Mandate'),
        formDefinitionMap[EEditListingForms.MANDATE](),
      ),
    ],
    // Property
    [
      EEditListingForms.ADDRESS,
      getFormDefinition<TPropertyAddressFormData>(
        t('Address'),
        formDefinitionMap[EEditListingForms.ADDRESS](),
      ),
    ],
    [
      EEditListingForms.PROPERTY,
      getFormDefinition<PropertyFormDetailsData, PropertyFormContext>(
        t('Property details'),
        formDefinitionMap[EEditListingForms.PROPERTY](),
        getValidatePropertyDetailsFormData(t),
      ),
    ],
    [
      EEditListingForms.POI,
      getFormDefinition<PropertyPoiFragment>(
        t('Points of interest nearby'),
        formDefinitionMap[EEditListingForms.POI](),
      ),
    ],
  ]);

  // If not Land.
  if (listing.property.__property_type?.main_type !== 'PROP') {
    listingFormDefinition.set(
      EEditListingForms.EQUIPMENT,
      getFormDefinition<PropertyFeaturesFragment>(
        t('Equipment & Characteristics'),
        formDefinitionMap[EEditListingForms.EQUIPMENT](),
        null,
        'sm',
      ),
    );
  }

  // Rented room: only for a single-room rental.
  if (
    listing.offer_type === 'rent' &&
    listing.property.__property_type?.name === 'apartment_single_room'
  ) {
    listingFormDefinition.set(
      EEditListingForms.RENTED_ROOM,
      getFormDefinition<TRentedRoomFormData, PropertyFormContext>(
        t('Rented room'),
        formDefinitionMap[EEditListingForms.RENTED_ROOM](),
        null,
        'sm',
      ),
    );
  }

  return listingFormDefinition;
};

type ListingFormDefinitionMap = ReturnType<typeof getFormDefinitionMap>;

export const useListingOverviewEditFormDefinition = <
  Key extends keyof ListingFormDefinitionMap,
>({
  listing,
  formType,
}: {
  listing?: ListingData | null;
  formType: Key;
}) => {
  const { countryCode, locale } = useLocale();
  const { me } = useAppData();

  const isCHTenant = me?.tenant.country_code === 'CH';

  const memoizedGetFormDefinitionMap = useCallback(
    () =>
      listing != null
        ? getFormDefinitionMap(
            listing,
            isCHTenant,
            countryCode,
            isCHTenant,
            locale,
          )
        : null,
    [listing, countryCode, locale, isCHTenant],
  );

  const formDefinition = useMemo(() => {
    const formDefinitionMap = memoizedGetFormDefinitionMap();

    if (formDefinitionMap == null) {
      return null;
    }

    return formDefinitionMap[formType]();
  }, [memoizedGetFormDefinitionMap, formType]);

  return formDefinition as ReturnType<ListingFormDefinitionMap[Key]> | null;
};
